import {
  Box,
  Switch,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../Dashboard/Header";
import CancelIcon from "@mui/icons-material/Cancel";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { tokens } from "../../theme";

// ------------------------------------------------------------------------
const PREFERENCE_KEY = "infiniteAppUserPreferences";

// ------------------------------------------------------------------------
export default function TallyCompanies() {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("Role");
  const [open, setOpen] = useState("");
  const [MailSendOTP, setMailSendOTP] = useState("");
  const [VerityOTP, setVerityOTP] = useState("");
  const [EnterOTP, setEnterOTP] = useState("");
  const [DelCmpId, setDelCmpId] = useState("");

  // The *static* module catalogue for the logged-in admin (kept in localStorage)
  const modData = JSON.parse(localStorage.getItem("ModData")) || [];

  // --------------------------------------------------------------------
  /* User-specific table look-and-feel */
  const savedPrefs = JSON.parse(localStorage.getItem(PREFERENCE_KEY) || "{}");
  const preferences = Object.keys(savedPrefs).length
    ? savedPrefs
    : {
        rowHeight: 32, // Reduced from 40 to 32
        fontFamily: "Source Sans Pro",
        fontSize: 14, // Reduced from 16 to 14
        tableAlign: "center",
      };

  // --------------------------------------------------------------------
  /* Component state */
  const [selectedId, setSelectedId] = useState(null); // company row selection
  const [selectedName, setSelectedName] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(""); // Cmp_Id currently in the dialog
  const [openModule, setOpenModule] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [CmpNames, setCmpNames] = useState([]);
  const [moduleStates, setModuleStates] = useState({}); // { moduleId: bool } FOR THE DIALOG ONLY

  // --------------------------------------------------------------------
  /* INITIAL DATA FETCH (company list & global module catalogue) */
  useEffect(() => {
    fetchCompanyList();
    // No need to fetch module states here – they are company-specific
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------------------------------------------------------
  const handleMdClose = () => setOpenModule(false);

  const handleToggleChange = (moduleId, status) =>
    setModuleStates((prev) => ({ ...prev, [moduleId]: status }));

  const handleSelect = (id, Cmp_Name) => {
    setSelectedId(selectedId === id ? null : id);
    setSelectedName(selectedName === Cmp_Name ? null : Cmp_Name);
    localStorage.setItem("cmpid", id);
    localStorage.setItem("cmpname", Cmp_Name);
    window.location.reload(); // preserves legacy workflow
  };

  // --------------------------------------------------------------------
  /* Fetch all companies */
  const fetchCompanyList = async () => {
    if (!token) {
      navigate("/Auth/");
      return;
    }
    try {
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + "/company/list",
        {},
        { headers: { Authorization: token } }
      );
      // console.log(data.companie)
      setCmpNames(data.companies);
    } catch (error) {
      console.error("Error fetching company list:", error);
    }
  };

  // --------------------------------------------------------------------
  /* NEW: Fetch ENABLED states for a single company */
  const fetchEnabledStates = async (cmpid) => {
    try {
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + "/module/enabledModuleslist",
        { cmpid },
        { headers: { Authorization: token } }
      );

      const states = {};
      Object.values(data || {}).forEach((m) => {
        states[m.module_id] = Number(m.status ?? 1) === 1;
      });
      setModuleStates(states);
    } catch (error) {
      console.error("Error fetching enabled module states:", error);
      setModuleStates({}); // empty so nothing looks enabled inadvertently
    }
  };

  // --------------------------------------------------------------------
  const handleMdOpen = (cmpid, isActiveRow) => {
    if (!isActiveRow) {
      Swal.fire({
        title: "Warning",
        text: "Please activate the selected row company.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#f0ad4e",
        width: 300,
        padding: "1em",
        customClass: { popup: "small-alert" },
      });
      return;
    }

    setSelectedCompany(cmpid);
    fetchEnabledStates(cmpid);
    setOpenModule(true);
  };

  // --------------------------------------------------------------------
  /* Submit toggles to API */
  const handleEnableMod = async () => {
    const payload = Object.entries(moduleStates).map(([moduleId, checked]) => ({
      cmpid: selectedCompany,
      moduleid: Number(moduleId),
      status: checked ? 1 : 0,
    }));

    try {
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + "/module/enableModules",
        payload,
        { headers: { Authorization: token } }
      );

      // Reflect changes in localStorage copy – ONLY for the modules we toggled
      const updatedModData = modData.map((m) => ({
        ...m,
        isEnabled: moduleStates[m.module_Id] ? 1 : 0,
      }));
      localStorage.setItem("ModData", JSON.stringify(updatedModData));

      Swal.fire({
        title: "Enabled!",
        text: data.message,
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#28a745",
        width: 300,
        padding: "1em",
        customClass: { popup: "small-alert" },
      }).then(() => window.location.reload());
    } catch (error) {
      console.error("Error enabling modules:", error);
    }
    handleMdClose();
  };

  // --------------------------------------------------------------------
  /* Helpers */
  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}-${pad(
      d.getMonth() + 1
    )}-${d.getFullYear()}  ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // --------------------------------------------------------------------
  /* DATA-GRID rows & columns */
  const CompanyListRow = CmpNames.map((c) => ({
    id: c.Cmp_Id,
    Cmp_Name: c.Cmp_Name,
    last_sync: formatTimestamp(c.updated_At),
  }));

  const columns = [
    {
      field: "Sr No",
      headerName: "Sr No",
      headerAlign: preferences.tableAlign,
      align: preferences.tableAlign,
      flex: 0.4,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (p) => p.api.getRowIndex(p.id) + 1,
    },
    {
      field: "Cmp_Name",
      headerName: "Company Name",
      headerAlign: preferences.tableAlign,
      align: "Left",
      flex: 2,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "last_sync",
      headerName: "last Sync",
      headerAlign: preferences.tableAlign,
      align: preferences.tableAlign,
      flex: 0.6,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "Action",
      headerName: "Action",
      headerAlign: preferences.tableAlign,
      align: preferences.tableAlign,
      flex: 1.3,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        const isSelected = localStorage.getItem("cmpid") === row.id;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="contained"
              size="small"
              style={{
                backgroundColor: isSelected ? "green" : colors.blueAccent[700],
                color: isSelected ? "white" : colors.primary[150],
                height: 18, // reduced height
                padding: "2px 6px", // tighter padding
                fontSize: "12px", // smaller font for compactness
                minHeight: "unset", // overrides default MUI small button height
              }}
              onClick={() => handleSelect(row.id, row.Cmp_Name)}
            >
              {isSelected ? "Active" : "Select"}
            </Button>

            {userRole === "admin" && (
              <Button
                variant="contained"
                size="small"
                style={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.primary[150],
                  height: 18,
                  padding: "2px 6px",
                  fontSize: "12px",
                  minHeight: "unset",
                }}
                onClick={() => handleMdOpen(row.id, isSelected)}
              >
                Add Module
              </Button>
            )}

            {userRole === "admin" && (
              <Button
                variant="contained"
                size="small"
                style={{
                  backgroundColor: "black",
                  color: "white",
                  height: 18,
                  padding: "2px 6px",
                  fontSize: "12px",
                  minHeight: "unset",
                }}
                onClick={() => {
                  setOpen(true);
                  setDelCmpId(row);
                }}
              >
                Delete Company
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  // --------------------------------------------------------------------
  /* Dialog grid rows */
  const moduleRows = modData.map((m, idx) => ({
    id: m.module_Id,
    module_Name: m.module_Name,
    is_Enabled: m.isEnabled,
    sr_no: idx + 1,
  }));

  const moduleColumns = [
    {
      field: "Sr No",
      headerName: "Sr No",
      headerAlign: preferences.tableAlign,
      align: preferences.tableAlign,
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (p) => p.api.getRowIndex(p.id) + 1,
    },
    {
      field: "module_Name",
      headerName: "Module List",
      headerAlign: preferences.tableAlign,
      align: preferences.tableAlign,
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "Actions",
      headerName: "Actions",
      headerAlign: preferences.tableAlign,
      align: preferences.tableAlign,
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        const checked = moduleStates[row.id] || false;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Switch
              size="small" // Added small size
              checked={checked}
              onChange={(e) => handleToggleChange(row.id, e.target.checked)}
              color="success"
            />
            <span
              style={{
                color: checked
                  ? colors.greenAccent[500]
                  : colors.redAccent[500],
                fontSize: 15, // Smaller font
              }}
            >
              {checked ? "ENABLED" : "DISABLED"}
            </span>
          </Box>
        );
      },
    },
  ];

  // =======handle send  OTP For delete company

  const handleSendOTP = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/company/deleteCmpsendOTP`,
        { userEmail: MailSendOTP },
        { headers: { Authorization: token } }
      );
      setOpen(false);
      // console.log("data : ", response.data.message);
      Swal.fire({
        title: "Send!",
        text: response.data.message,
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#28a745",
        width: "300px",
        padding: "1em",
        customClass: {
          popup: "small-alert",
        },
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: true,

        didOpen: () => {
          const confirmButton = Swal.getConfirmButton();
          confirmButton.focus();
          window.handleEnterKey = (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              Swal.clickConfirm();
              document.removeEventListener("keydown", window.handleEnterKey);
            }
          };

          document.addEventListener("keydown", window.handleEnterKey);
        },

        willClose: () => {
          document.removeEventListener("keydown", window.handleEnterKey);
        },
      }).then((result) => {
        if (result.isConfirmed) {
          setVerityOTP(true);
          setOpen(true);
        }
      });
      setMailSendOTP("");
    } catch (err) {
      console.log(err);
    }
  };

  // ======= handle Verify OTP For delete company

  const handleVerifyOTP = async () => {
    const cmpdelBody = {
      otp: EnterOTP,
      cmpid: DelCmpId.id,
    };
    console.log("cmpdelBody", cmpdelBody);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/company/deleteCmpOtpVerify`,
        cmpdelBody,
        { headers: { Authorization: token } }
      );
      setOpen(false);
      setEnterOTP("");
      setVerityOTP(false);
      Swal.fire({
        title: "Success!",
        text: response.data.message,
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#28a745",
        width: "300px",
        padding: "1em",
        customClass: {
          popup: "small-alert",
        },
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
           window.location.reload();
        }
      });
    } catch (err) {
      console.log(err);
    }
  };

  // --------------------------------------------------------------------
  return (
    <>
      {/* -------- Module-configuration dialog -------- */}
      <Dialog
        open={openModule}
        onClose={handleEnableMod}
        PaperProps={{ sx: { width: 700, maxWidth: "80%" } }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 0, m: 0 }}
        >
          <DialogTitle
            sx={{ fontSize: 20, p: "8px 12px", m: 0, lineHeight: 1.2 }}
          >
            Module Configuration
          </DialogTitle>
          <CancelIcon
            sx={{ cursor: "pointer", fontSize: 22, mr: 1.5, mt: 1 }}
            onClick={handleMdClose}
          />
        </Box>

        {/* Content Section */}
        <DialogContent sx={{ p: 0, m: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: 350,
              m: 0,
              p: 0,
              gap: 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                "& .MuiDataGrid-root": { border: "none" },
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                  padding: "2px 6px",
                  lineHeight: "25px",
                  fontSize: "17px",
                },
                "& .MuiDataGrid-row": {
                  margin: "8px 0", // Added top and bottom margin for rows
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                    },
                  },
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: colors.blueAccent[700],
                  borderBottom: "none",
                  color: colors.primary[150],
                  fontSize: 18,
                  minHeight: 30,
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "none",
                  backgroundColor: colors.blueAccent[700],
                  color: colors.primary[150],
                  minHeight: "32px !important",
                  "& .MuiTablePagination-root": {
                    minHeight: "32px !important",
                    marginTop: "-1px",
                  },
                  "& .MuiTablePagination-toolbar": {
                    minHeight: "32px !important",
                    alignItems: "center",
                    padding: "0 8px",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    margin: 0,
                    fontSize: "13px",
                    lineHeight: "32px",
                    color: colors.primary[150],
                  },
                  "& .MuiTablePagination-displayedRows": {
                    margin: 0,
                    fontSize: "13px",
                    lineHeight: "32px",
                    color: colors.primary[150],
                  },
                  "& .MuiTablePagination-select": {
                    fontSize: "13px",
                    padding: "2px 8px",
                    margin: "0 4px",
                    color: colors.primary[150],
                  },
                  "& .MuiInputBase-root": {
                    color: colors.primary[150],
                    "& .MuiSvgIcon-root": {
                      fontSize: "18px",
                      color: colors.primary[150],
                    },
                  },
                  "& .MuiTablePagination-actions": {
                    marginLeft: "8px",
                    "& .MuiIconButton-root": {
                      padding: "4px",
                      "& svg": {
                        fontSize: "20px",
                        color: colors.primary[150],
                      },
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    },
                  },
                },
              }}
            >
              <DataGrid
                rows={moduleRows}
                columns={moduleColumns}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                rowsPerPageOptions={[20, 50, 100]}
                pagination
                disableColumnSort
                density="compact"
                rowHeight={30}
                headerHeight={45}
              />
            </Box>

            {/* Button Section */}
            <Box
              display="flex"
              justifyContent="flex-end"
              sx={{ p: "4px 8px", m: 0 }}
            >
              <Button
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.primary[150],
                  textTransform: "none",
                  minHeight: "28px",
                  fontSize: "13px",
                }}
                onClick={handleEnableMod}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ------------- Main Companies grid ------------- */}
      <Box mt="70px" mr="20px">
        <Header title="Tally Companies" />
        <Box
          height="83vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              cursor: "pointer",
            },
            "& .name-column--cell": {
              color: colors.greenAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
              color: colors.primary[120],
              cursor: "default",
            },
            "& .MuiDataGrid-columnHeader": {
              padding: "0 !important",
            },
            "& .MuiDataGrid-columnHeader:focus": {
              outline: "none",
              boxShadow: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              outline: "none",
              boxShadow: "none",
            },
            "& .MuiDataGrid-columnHeader:focus-visible": {
              outline: "none",
              boxShadow: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
              color: colors.primary[150],
            },
            // ✅ Zoom on hover using a class
            "& .TCmpLzoom-row:hover": {
              transform: "scale(1.01)",
              transition: "transform 0.2s ease-in-out",
              zIndex: 1,
              backgroundColor: colors.blueAccent[900],
            },
          }}
        >
          <DataGrid
            rows={CompanyListRow}
            columns={columns}
            disableColumnSort
            getRowClassName={() => "TCmpLzoom-row"}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[20, 50, 100]}
            pagination
            rowHeight={35} // Reduced from 30
            headerHeight={35} // Reduced from 40
            density="compact"
            sx={{
              "& .MuiDataGrid-cell": {
                padding: "0px 6px", // Reduced padding
                lineHeight: "20px", // Reduced line height
                fontSize: "14px", // Reduced font size
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: colors.blueAccent[700],
                margin: "0 !important",
                padding: "0 !important",
              },
              "& .MuiDataGrid-columnHeader": {
                whiteSpace: "normal !important",
                lineHeight: "1.2 !important",
                padding: "2px 6px !important", // Reduced padding
                margin: "0 !important",
                height: "35px !important", // Reduced height
                minHeight: "35px !important", // Reduced min-height
                maxHeight: "35px !important", // Reduced max-height
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "14px", // Reduced font size
                fontWeight: "bold",
                whiteSpace: "normal !important",
                lineHeight: "1.2 !important",
                textAlign: "center",
                overflow: "visible !important",
                textOverflow: "unset !important",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                margin: "0 !important",
                padding: "0 !important",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
            componentsProps={{
              footer: {
                sx: {
                  height: 30, // Reduced footer height
                  minHeight: 30,
                },
              },
            }}
          />
        </Box>
      </Box>
      {/* ==================================== */}

      <Dialog open={open} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, textAlign: "center" }}>
            {VerityOTP
              ? "Please enter the OTP to sent your Email Id."
              : "Please enter the Mail Id to sent OTP."}
          </Typography>

          <Box display="flex" justifyContent="center">
            {VerityOTP ? (
              <TextField
                label="Enter Your OTP"
                autoFocus
                fullWidth
                type="password"
                variant="outlined"
                value={EnterOTP}
                onChange={(e) => setEnterOTP(e.target.value)}
              />
            ) : (
              <TextField
                label="Enter Your Email ID"
                autoFocus
                fullWidth
                type="email"
                variant="outlined"
                value={MailSendOTP}
                onChange={(e) => setMailSendOTP(e.target.value)}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={VerityOTP ? handleVerifyOTP : handleSendOTP}
            variant="contained"
            disabled={
              VerityOTP ? EnterOTP.length !== 6 : MailSendOTP.length === 0
            }
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.primary[150],
              "&:hover": {
                backgroundColor: colors.blueAccent[700],
                color: colors.primary[150],
              },
            }}
          >
            {VerityOTP ? "Verify OTP" : "Send OTP"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
